import { useState, useRef, useEffect } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import api from '../../utils/apiClient';
import APIConstant from '../../utils/pathConstants';
import Toast from '../Shared/Toast/Toast';
import Loader from '../Shared/Loader/Loader';
import logo from '../../assets/images/dune-main-logo.svg';
import portlinkLogo from '../../assets/images/portlinkLogo.svg';
import { useNavigate } from 'react-router-dom';
import { Dialog } from 'primereact/dialog';
import CommonConfig from '../../utils/constant';
import AccountStore from '../../utils/accountStore';
import useRecaptchaV2 from '../../utils/useRecaptchaV2';

const Login = () => {
  const initialState = {
    userName: '',
    password: '',
    accounts: [],
    selectedUserId: null,
    selectedSchoolName: '',
    userNameErr: false,
    UserhelperText: '',
    passwordErr: false,
    passwordhelperText: '',
    IsPasswordSeen: false,
    openLoginConfirm: false,
    openSchoolSelect: false,
    passwordRetry: false,
    openSessionLimit: false,
    activeSessions: [],
    pendingLoginOpts: null,
  };

  const [state, setState] = useState(initialState);
  const navigate = useNavigate();
  const continueBtnRef = useRef(null);
  const captcha = useRecaptchaV2();
  const pendingLoginRef = useRef(null);

  useEffect(() => {
    if (captcha.visible) captcha.render();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captcha.visible]);

  const handleCheck = (e, type) => {
    let value = e.target.value;

    if (type === 'userName') {
      setState((prevState) => ({
        ...prevState,
        userName: value,
        userNameErr: value === '' || value === null,
        UserhelperText: value === '' || value === null ? 'Username is required' : '',
        passwordRetry: false,
        selectedUserId: null,
        selectedSchoolName: '',
        accounts: [],
      }));
    }

    if (type === 'Password') {
      setState((prevState) => ({
        ...prevState,
        password: value,
        passwordErr: value === '' || value === null,
        passwordhelperText: value === '' || value === null ? 'Password is required' : '',
      }));
    }
  };

  const validateIdentifier = () => {
    if (state.userName === '' || state.userName === null) {
      setState((prevState) => ({ ...prevState, userNameErr: true, UserhelperText: 'Username is required' }));
      return false;
    }
    return true;
  };

  const validatePassword = () => {
    if (state.password === '' || state.password === null) {
      setState((prevState) => ({ ...prevState, passwordErr: true, passwordhelperText: 'Password is required' }));
      return false;
    }
    return true;
  };

  const getRecaptchaToken = () => {
    return new Promise((resolve, reject) => {
      if (!window.grecaptcha) {
        return reject(new Error('reCAPTCHA not loaded'));
      }

      window.grecaptcha.ready(() => {
        window.grecaptcha.execute(process.env.REACT_APP_GOOGLE_SITE_KEY, { action: 'login' })
          .then(token => {
            if (!token) {
              reject(new Error('Failed to get reCAPTCHA token'));
            } else {
              resolve(token);
            }
          })
          .catch(err => reject(err));
      });
    });
  };

  // Completes the login using the entered credentials. `opts.userId`/`opts.accounts`
  // are passed explicitly to avoid reading not-yet-committed React state.
  const callLoginUser = async (isAlready, opts = {}) => {
    const v2Token = captcha.tokenRef.current;
    let token = null;
    try {
      token = await getRecaptchaToken();
    } catch (e) {
      if (!v2Token) throw e;
    }
    const uid = opts.userId !== undefined ? opts.userId : state.selectedUserId;
    const accs = opts.accounts !== undefined ? opts.accounts : state.accounts;
    const sname = opts.schoolName !== undefined ? opts.schoolName : state.selectedSchoolName;

    const loginData = {
      Phone: state.userName,
      Password: state.password,
      DeviceType: 'Web',
      DeviceId: CommonConfig.getDeviceId(),
      isAlreadyLogin: isAlready,
      IsOTPLogin: false,
      UserType: 'User',
      RecaptchaToken: token,
    };
    if (uid) {
      loginData.UserId = uid;
    }
    if (v2Token) {
      loginData.RecaptchaV2Token = v2Token;
      captcha.tokenRef.current = null;
    }

    const res = await api.post(APIConstant.path.login, loginData);
    captcha.hide();

    if (res?.sessionLimitReached) {
      setState((p) => ({
        ...p,
        openSessionLimit: true,
        activeSessions: res.sessions || [],
        pendingLoginOpts: { isAlready, opts },
      }));
      return;
    }

    if (res.success && res?.data && !CommonConfig.isEmpty(res?.data?.Token)) {
      const userData = res.data;
      localStorage.setItem('ERPLoginData', JSON.stringify(userData));
      CommonConfig.setCookie('selectedAcademicYear', userData?.SelectedAcademicYear ?? '');
      CommonConfig.setCookie('access_token', userData?.Token?.toString());
      CommonConfig.setCookie('refresh_token', userData?.RefreshToken?.toString());
      CommonConfig.setCookie('refreshValue', userData?.RefreshValue?.toString());
      CommonConfig.setCookie('loggedIn', true.toString());

      // Persist this school account + the identity's school list for the switcher.
      AccountStore.saveActiveAccount(userData);
      if (accs?.length) {
        AccountStore.setDiscoveredSchools(accs);
      }

      setTimeout(async () => {
        if (userData?.UserRole === 'Parent') {
          await CommonConfig.getUserMenu(userData, navigate, 'Login');
        } else {
          await CommonConfig.getUserMenu(userData, navigate, 'OnClick');
        }
      }, 2500);

      handleClose();
      Toast.success({ message: res.message });
    } else {
      const wrongForSelected = !!uid && accs?.length > 1;
      if (wrongForSelected) {
        setState((p) => ({
          ...p,
          passwordRetry: true,
          selectedUserId: uid,
          selectedSchoolName: sname,
          accounts: accs,
        }));
        Toast.error({
          message: sname
            ? `Incorrect password for ${sname}. Please re-enter the password for this school.`
            : res.message,
        });
      } else {
        Toast.error({ message: res.message });
      }
    }
  };

  // Handles the session-conflict dance + login. Catches the 409 to raise the dialog.
  const proceedLogin = async (isAlready, opts = {}) => {
    Loader.show();
    try {
      if (isAlready) {
        const sessionRes = await deleteUserSession(state?.userName);
        if (sessionRes?.success) {
          Toast.success({ message: sessionRes?.message ?? 'Previous session logged out successfully' });
        } else {
          Toast.error({ message: sessionRes?.message || 'Failed to logout previous session' });
        }
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
      await callLoginUser(isAlready, opts);
    } catch (err) {
      if (err?.captchaFallback) {
        pendingLoginRef.current = { isAlready, opts };
        captcha.show(() => proceedLogin(
          pendingLoginRef.current?.isAlready ?? false,
          pendingLoginRef.current?.opts ?? {},
        ));
        Toast.error({ message: 'Please confirm you are not a robot to continue.' });
      } else if (err?.statusCode === 409) {
        setState((prevState) => ({ ...prevState, openLoginConfirm: true }));
      } else {
        Toast.error({ message: err.message || 'Login failed' });
      }
    } finally {
      Loader.hide();
    }
  };

  // Sign In: identifier + password entered -> check identity, then branch.
  const handleLogin = async (e) => {
    e?.preventDefault?.();
    const okId = validateIdentifier();
    const okPw = validatePassword();
    if (!okId || !okPw) return;

    if (state.passwordRetry && state.selectedUserId) {
      await proceedLogin(false, {
        userId: state.selectedUserId,
        accounts: state.accounts,
        schoolName: state.selectedSchoolName,
      });
      return;
    }

    Loader.show();
    let opts = { userId: null, accounts: [] };
    try {
      const res = await api.post(APIConstant.path.checkLoginIdentifier, { identifier: state.userName, Password: state.password });

      // Password is verified before the school list is revealed.
      if (res?.success === false) {
        Toast.error({ message: res.message || 'Login failed' });
        Loader.hide();
        return;
      }

      // Multi-school identity -> open the school picker (login completes after a school is chosen).
      // Every other outcome (single school, non-school) logs in now.
      if (res?.success && res?.multi && Array.isArray(res.accounts) && res.accounts.length > 1) {
        setState((p) => ({ ...p, accounts: res.accounts, selectedUserId: null, selectedSchoolName: '', openSchoolSelect: true }));
        Loader.hide();
        return;
      }
      if (res?.success && res?.multi && Array.isArray(res.accounts) && res.accounts.length === 1) {
        const only = res.accounts[0];
        setState((p) => ({ ...p, accounts: res.accounts, selectedUserId: only.userId, selectedSchoolName: only.schoolName }));
        opts = { userId: only.userId, accounts: res.accounts, schoolName: only.schoolName };
      } else {
        setState((p) => ({ ...p, accounts: [], selectedUserId: null, selectedSchoolName: '' }));
        opts = { userId: null, accounts: [] };
      }
    } catch (err) {
      // Best-effort: proceed with normal login on any check error.
      setState((p) => ({ ...p, accounts: [], selectedUserId: null, selectedSchoolName: '' }));
      opts = { userId: null, accounts: [] };
    }
    Loader.hide();
    await proceedLogin(false, opts);
  };

  // School picked in the popup -> log in with the entered password.
  const handleSelectSchool = (acc) => {
    setState((p) => ({ ...p, openSchoolSelect: false, selectedUserId: acc.userId, selectedSchoolName: acc.schoolName }));
    proceedLogin(false, { userId: acc.userId, accounts: state.accounts, schoolName: acc.schoolName });
  };

  // At the max-session cap the user picks a device to log out, then we retry
  // the login with the same credentials.
  const handleRemoveSession = async (sessionId) => {
    const pending = state.pendingLoginOpts;
    try {
      Loader.show();
      const res = await api.post(APIConstant.path.deleteSessionById, { sessionId });
      Loader.hide();
      if (!res.success) {
        Toast.error({ message: res.message || 'Failed to log out that device' });
        return;
      }
      setState((p) => ({ ...p, openSessionLimit: false, activeSessions: [], pendingLoginOpts: null }));
      await proceedLogin(pending?.isAlready ?? false, pending?.opts ?? {});
    } catch (err) {
      Loader.hide();
      Toast.error({ message: err.message || 'Failed to log out that device' });
    }
  };

  const formatLastActive = (d) => {
    if (!d) return '';
    const diff = Date.now() - new Date(d).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'active just now';
    if (m < 60) return `active ${m} min ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `active ${h} hr ago`;
    const days = Math.floor(h / 24);
    return `active ${days} day${days > 1 ? 's' : ''} ago`;
  };

  const deleteUserSession = async (userId) => {
    try {
      const data = { username: userId };
      if (state.selectedUserId) {
        data.UserId = state.selectedUserId;
      }
      const res = await api.post(APIConstant.path.deleteSession, data);
      return res;
    } catch (err) {
      console.error('Error deleting session:', err);
      return { success: false, message: err.message };
    }
  };

  const handleClose = () => {
    setState((prevState) => ({
      ...prevState,
      openLoginConfirm: false,
    }));
  };

  const exitPasswordRetry = () => {
    setState((prevState) => ({
      ...prevState,
      passwordRetry: false,
      selectedUserId: null,
      selectedSchoolName: '',
      accounts: [],
      password: '',
      passwordErr: false,
      passwordhelperText: '',
    }));
  };

  const renderFooter = () => (
    <div className="dialog-footer">
      <Button
        label="Cancel"
        icon="pi pi-times"
        className="btn-secondary"
        onClick={handleClose}
        outlined
      />
      <Button
        ref={continueBtnRef}
        label="Continue"
        icon="pi pi-check"
        className="btn-primary"
        onClick={() => proceedLogin(true)}
      />
    </div>
  );

  return (
    <div className='auth-container login-split'>

      {/* ═══ LEFT — Navy brand-story panel ═══ */}
      <div className='ls-brandpanel'>
        {/* decorative arcs */}
        <div className='ls-arc ls-arc1' />
        <div className='ls-arc ls-arc2' />
        <div className='ls-arc ls-arc3' />

        {/* brand lockup */}
        <div className='ls-bp-logo'>
          <div className='ls-bp-badge'><img src={logo} alt='Dunes' /></div>
          {/* <div>
            <div className='ls-bp-wm'>Dun<span>es</span></div>
            <div className='ls-bp-cat'>Smart Campus Operating System</div>
          </div> */}
        </div>

        <div className='ls-bp-hero' style={{ marginTop: 'auto', marginBottom: 'auto', width: '100%', maxWidth: '540px' }}>

          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.2rem', marginTop: '2rem' }}>
            <div style={{ height: '1.5px', width: '32px', background: '#E9C96A' }}></div>
            <span style={{ fontSize: '0.75rem', fontWeight: 600, letterSpacing: '2px', color: '#B3C4DE', textTransform: 'uppercase' }}>STUDENT PORTAL</span>
          </div>

          <h1 style={{ fontSize: '3.2rem', lineHeight: '1.15', letterSpacing: '-0.02em', fontWeight: 700, margin: '0' }}>
            Learn Today.<br /><span style={{ color: '#E9C96A' }}>Lead Tomorrow.</span>
          </h1>

          <p style={{ marginTop: '1.8rem', lineHeight: '1.7', fontSize: '1.05rem', color: '#DCE4F5', fontWeight: 400, paddingRight: '2rem' }}>
            Your central hub for learning resources, live classes, academic support and everything you need to keep your college journey moving forward.
          </p>

          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '2.5rem', marginBottom: '2.5rem' }}>
            <div style={{ height: '1.5px', width: '32px', background: '#E9C96A' }}></div>
            <span style={{ fontSize: '0.75rem', fontWeight: 600, letterSpacing: '2px', color: '#B3C4DE', textTransform: 'uppercase' }}>EDUCATION EMPOWERS TOMORROW</span>
          </div>

          <div style={{ display: 'flex', gap: '2.5rem', marginTop: '1rem' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              <i className="pi pi-book" style={{ fontSize: '1.8rem', color: '#fff' }}></i>
              <div style={{ lineHeight: '1.3' }}>
                <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#fff' }}>Learn</div>
                <div style={{ fontSize: '0.75rem', color: '#B3C4DE' }}>Without Limits</div>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              <i className="pi pi-users" style={{ fontSize: '1.8rem', color: '#fff' }}></i>
              <div style={{ lineHeight: '1.3' }}>
                <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#fff' }}>Connect</div>
                <div style={{ fontSize: '0.75rem', color: '#B3C4DE' }}>With Your Campus</div>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              <i className="pi pi-chart-bar" style={{ fontSize: '1.8rem', color: '#fff' }}></i>
              <div style={{ lineHeight: '1.3' }}>
                <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#fff' }}>Grow</div>
                <div style={{ fontSize: '0.75rem', color: '#B3C4DE' }}>For a Brighter Future</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ═══ RIGHT — White sign-in panel ═══ */}
      <div className='ls-formpanel'>
        <div className='ls-formbox'>
          <h2 className='ls-title'>Sign in</h2>
          <p className='ls-subtitle'>
            {state.passwordRetry
              ? 'Enter the password for the selected school to continue.'
              : 'Welcome back — please enter your details.'}
          </p>

          <form onSubmit={handleLogin} className='ls-form'>
            {state.passwordRetry ? (
              <button type='button' className='ls-school-item ls-school-item--active' onClick={exitPasswordRetry}>
                <span className='ls-school-ic'><i className='pi pi-building' /></span>
                <span className='ls-school-meta'>
                  <span className='ls-school-name'>{state.selectedSchoolName || 'Selected school'}</span>
                  <span className='ls-school-sub'>Use a different account</span>
                </span>
                <i className='pi pi-times' />
              </button>
            ) : (
              <div className='ls-field-group'>
                <label htmlFor='emailAddress'>Username / Email</label>
                <div className={`ls-field ${state.userNameErr ? 'ls-field--err' : ''}`}>
                  <i className='pi pi-user' />
                  <InputText
                    id='emailAddress'
                    placeholder='Enter your username or email'
                    type='text'
                    maxLength={50}
                    value={state.userName}
                    onChange={(e) => handleCheck(e, 'userName')}
                  />
                </div>
                {state.userNameErr && <span className='ls-err'>{state.UserhelperText}</span>}
              </div>
            )}

            <div className='ls-field-group'>
              <label htmlFor='password'>Password</label>
              <div className={`ls-field ${state.passwordErr ? 'ls-field--err' : ''}`}>
                <i className='pi pi-lock' />
                <InputText
                  id='password'
                  type={state.IsPasswordSeen ? 'text' : 'password'}
                  placeholder='Enter your password'
                  maxLength={20}
                  value={state.password}
                  onChange={(e) => handleCheck(e, 'Password')}
                />
                <button
                  type='button'
                  className='ls-eye'
                  onClick={() => setState((p) => ({ ...p, IsPasswordSeen: !p.IsPasswordSeen }))}
                >
                  <i className={state.IsPasswordSeen ? 'pi pi-eye-slash' : 'pi pi-eye'} />
                </button>
              </div>
              {state.passwordErr && <span className='ls-err'>{state.passwordhelperText}</span>}
              {state.passwordRetry && !state.passwordErr && (
                <span className='ls-err'>
                  {state.selectedSchoolName
                    ? `Incorrect password for ${state.selectedSchoolName}. Please re-enter the password for this school.`
                    : 'Incorrect password. Please re-enter the password for the selected school.'}
                </span>
              )}
            </div>

            <div className='ls-row'>
              <label className='ls-remember'><input type='checkbox' />Remember me</label>
              <span className='ls-link'>Forgot password?</span>
            </div>

            <div style={{ display: captcha.visible ? 'block' : 'none', marginTop: '1rem' }}>
              <div ref={captcha.containerRef} style={{ display: 'flex', justifyContent: 'center' }}></div>
            </div>

            <Button type='submit' className='ls-submit' label='Sign In' icon='pi pi-arrow-right' iconPos='right' />
          </form>

          <div className='ls-divider'><span>or</span></div>

          <Button
            label='Login with OTP'
            icon='pi pi-mobile'
            className='ls-otp-btn'
            outlined
            onClick={() => navigate('/otp-login')}
          />

          <p className='ls-footer' style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
            <span>&copy; {new Date().getFullYear()} Dunes · Powered By&nbsp;</span>
            <a href="https://portlinks.in" target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <img src={portlinkLogo} alt="PortLinks" style={{ height: '30px' }} />
              PortLinks India
            </a>
          </p>
        </div>
      </div>

      {/* School / College selection popup (multi-school identity) */}
      <Dialog
        visible={state.openSchoolSelect}
        onHide={() => setState((p) => ({ ...p, openSchoolSelect: false }))}
        header={
          <div className="dialog-header">
            <i className="pi pi-building" />
            <span className="dialog-title">Select your school</span>
          </div>
        }
        draggable={false}
        dismissableMask
        position="center"
        className="confirmation-dialog"
        style={{ width: '440px' }}
      >
        <div className='ls-school-list'>
          {state.accounts.map((acc) => (
            <button
              type='button'
              key={acc.userId}
              className='ls-school-item'
              onClick={() => handleSelectSchool(acc)}
            >
              <span className='ls-school-ic'><i className='pi pi-building' /></span>
              <span className='ls-school-meta'>
                <span className='ls-school-name'>{acc.schoolName || 'School'}</span>
                {acc.board ? <span className='ls-school-sub'>{acc.board}</span> : null}
              </span>
              <i className='pi pi-angle-right' />
            </button>
          ))}
        </div>
      </Dialog>

      {/* Max-session picker: choose a device to log out to free a slot */}
      <Dialog
        visible={state.openSessionLimit}
        onHide={() => setState((p) => ({ ...p, openSessionLimit: false, pendingLoginOpts: null }))}
        header={
          <div className="dialog-header">
            <i className="pi pi-desktop" />
            <span className="dialog-title">Logged in on 3 devices</span>
          </div>
        }
        draggable={false}
        dismissableMask
        position="center"
        className="confirmation-dialog"
        style={{ width: '460px' }}
      >
        <p style={{ margin: '0 0 12px', fontSize: '14px', color: '#6B7794' }}>
          You can be signed in on up to 3 devices. Choose one to log out and continue.
        </p>
        <div className='ls-school-list'>
          {state.activeSessions.map((s) => (
            <button
              type='button'
              key={s._id}
              className='ls-school-item'
              onClick={() => handleRemoveSession(s._id)}
            >
              <span className='ls-school-ic'><i className='pi pi-desktop' /></span>
              <span className='ls-school-meta'>
                <span className='ls-school-name'>{s.device || 'Unknown device'}</span>
                <span className='ls-school-sub'>{formatLastActive(s.lastActive)}</span>
              </span>
              <i className='pi pi-sign-out' />
            </button>
          ))}
        </div>
      </Dialog>

      <Dialog
        visible={state.openLoginConfirm}
        onHide={handleClose}
        header={
          <div className="dialog-header">
            <i className="pi pi-exclamation-triangle warning-icon"></i>
            <span className="dialog-title">Session Conflict</span>
          </div>
        }
        footer={renderFooter()}
        onShow={() => {
          setTimeout(() => {
            continueBtnRef.current?.focus();
          }, 50);
        }}
        draggable={false}
        dismissableMask
        position="center"
        className="confirmation-dialog"
        style={{ width: '440px' }}
      >
        <div className="dialog-content">
          <p className="dialog-message">
            You are already logged in on another device. Would you like to end that session and continue here?
          </p>
          <div className="session-info">
            <i className="pi pi-info-circle"></i>
            <span>This will log you out from all other active sessions</span>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default Login;
